import React from "react";
import { createUploadLink } from "apollo-upload-client";
import gql from "graphql-tag";
import config from "./config";

import { ApolloClient } from "apollo-client";
import { InMemoryCache } from "apollo-cache-inmemory";
import { onError } from "apollo-link-error";
import { ApolloLink } from "apollo-link";
import { getAccessToken, isTokenExpired } from "./utils/token";
import { handleAuthError } from "./components/auth";

// Session → JWT migration: attach the Bearer token per operation so a renewed
// token is picked up without recreating the client. An expired token is never
// sent — the server rejects a bad Bearer token with no session fallback.
const authLink = new ApolloLink((operation, forward) => {
  const token = getAccessToken();
  if (token && !isTokenExpired(token)) {
    operation.setContext(({ headers = {} }) => ({
      headers: { ...headers, authorization: `Bearer ${token}` }
    }));
  }
  return forward(operation);
});

export const apolloClient = new ApolloClient({
  link: ApolloLink.from([
    onError(({ graphQLErrors, networkError }) => {
      if (graphQLErrors) graphQLErrors.map(({ message, locations, path }) => console.log(`[GraphQL error]: Message: ${message}`, { locations, path }));
      if (networkError) console.log(`[Network error]: ${networkError}`);
    }),
    authLink,
    createUploadLink({
      uri: config.graphql_uri,
      credentials: "include"
    })
  ]),
  cache: new InMemoryCache()
});

export const serverStackClient = new ApolloClient({
  link: ApolloLink.from([
    onError(({ graphQLErrors, networkError }) => {
      if (graphQLErrors) {
        graphQLErrors.map(({ message, locations, path }) => console.log(`[GraphQL error]: Message: ${message}`, { locations, path }));
        handleAuthError(graphQLErrors);
      }
      if (networkError) console.log(`[Network error]: ${networkError}`);
    }),
    authLink,
    createUploadLink({
      uri: config.server_stack_url,
      credentials: "include"
    })
  ]),
  cache: new InMemoryCache()
});

// Default to an empty object so consumers that destructure (`{ locale }`,
// `{ switchLang }`) render outside AppContextProvider instead of throwing —
// AppHeader/AppSidebar mount their children lazily, ahead of the provider.
export const AppContext = React.createContext({});
export const QUERY_MASTER_DATA = gql`
  {
    masterdata
  }
`;
// `me` giờ đọc từ server-stack (V3) qua query `context` thay vì /graphql cũ:
// token JWT do server-stack phát, nên context phải hỏi chính API đã verify nó.
// Alias `_id: id` và `companies: _companies` giữ nguyên shape mọi consumer đang
// dùng (`me._id`, `me.companies`).
export const QUERY_ME = gql`
  {
    context {
      me {
        _id: id
        roles
        name
        email
        phone
        address
        facebook
        google
        twitter
        zendeskId
        image {
          original
          filename
          mime
          checksum
          cdnUri
        }
        gender
        companies: _companies {
          _id
          name
          plan
          inContract
          status
          canEditFreight
        }
      }
    }
  }
`;

export const MUTATION_LOGOUT = gql`
  mutation Logout {
    logout
  }
`;

export const QUERY_MAKES = gql`
  query makes($params: Object) {
    makes(params: $params) {
      _id
      name
      key
      carCount
    }
  }
`;

export const QUERY_MODELS = gql`
  query models($filters: Object, $offset: Int, $limit: Int, $sort: String, $order: String) {
    models(filters: $filters, offset: $offset, limit: $limit, sort: $sort, order: $order) {
      models {
        _id
        name
        key
        makeModelKey
        make
        locked
        alias {
          _id
          name
          key
          makeModelKey
        }
        carCount
      }
      total
    }
  }
`;

export const QUERY_HAS_NEW_DOC = gql`
  {
    hasNewDoc
  }
`;

export const withContext = Component => props => <AppContext.Consumer>{context => <Component {...props} {...context} />}</AppContext.Consumer>;
